<template>
  <transition
    appear
    enter-active-class="ease-in-out duration-100 transform"
    enter-from-class="translate-y-full"
    enter-to-class="translate-y-0"
    leave-active-class="ease-in-out duration-100 transform"
    leave-from-class="translate-y-0"
    leave-to-class="translate-y-full"
  >
    <div
      v-if="closeMethod === 'show' || visible"
      v-show="closeMethod === 'if' || visible"
      class="fixed bottom-0 z-30 w-full max-w-screen-md flex flex-col touch-none overscroll-contain rounded-t-10px bg-white shadow-function-panel"
      :class="isResizing ? 'opacity-90' : 'duration-200'"
      :style="{
        height: isResizable ? `calc(${panelVh} * var(--real1dvh))` : `${fixedHeight}px`,
        'max-height': isResizable ? '-webkit-fill-available' : `${fixedHeight}px`,
      }"
    >
      <!--    control -->
      <div
        v-if="isResizable"
        class="flex cursor-ns-resize items-center justify-center"
        :style="{
          'min-height': `${DRAG_BAR_HEIGHT}px`,
          height: `${DRAG_BAR_HEIGHT}px`,
        }"
        @pointerdown="startDrag"
      >
        <i class="icon-hamburger-two-line text-24px text-primary-80" />
      </div>
      <!--      close btn -->
      <div
        class="absolute right-8px top-4px flex cursor-pointer items-center justify-center p-4px text-primary-80"
        :class="{
          'h-32px': isResizable,
          'h-24px': !isResizable,
        }"
        @click="
          () => {
            pushGA4ButtonClick(`${panelKey} panel close dialog`)
            $emit('update:visible', false)
          }
        "
      >
        <p v-if="exitButtonText" class="title-12-h16-m">{{ exitButtonText }}</p>
        <p v-else-if="isResizable" class="title-12-h16-m">
          {{ $t('btn.close_dialog') }}
        </p>
        <i v-else class="icon-close text-24px" />
      </div>
      <!--      header -->
      <div class="header-slot flex items-center px-24px" :class="{ 'mt-24px': !isResizable }">
        <slot name="header" />
      </div>
      <!--    content -->
      <div
        ref="contentElement"
        class="scrollbar h-full"
        :class="{
          'overflow-auto': scrollBar === 'auto',
          'overflow-x-scroll': ['all', 'horizontal'].includes(scrollBar!),
          'overflow-x-hidden': ['hidden', 'vertical'].includes(scrollBar!),
          'overflow-y-scroll': ['all', 'vertical'].includes(scrollBar!),
          'overflow-y-hidden': ['hidden', 'horizontal'].includes(scrollBar!),
        }"
      >
        <slot name="content" />
      </div>
      <slot name="footer" />
    </div>
  </transition>
</template>

<script setup lang="ts">
import { onActivated, onDeactivated, onMounted, ref, useTemplateRef } from 'vue'

import { pushGA4ButtonClick } from '@/utils/googleAnalytics'
import { setMouseMoveEvent } from '@/utils/mouseMoveEvent'

const props = withDefaults(
  defineProps<{
    closeMethod?: 'if' | 'show'
    defaultVh?: number
    exitButtonText?: null | string
    // isResizable === false 才會使用到這個
    fixedHeight?: number
    isResizable?: boolean
    panelKey?: string
    // 因為有的會被文字太長擠壓到，所以多增加一個 `auto` 讓他自己變化吧...
    // 'auto' 跟 'hidden' 不會套用滑鼠拖曳拉動捲軸事件
    scrollBar?: 'all' | 'auto' | 'hidden' | 'horizontal' | 'vertical'
    visible?: boolean
  }>(),
  {
    visible: false,
    isResizable: false,
    defaultVh: 25,
    panelKey: '',
    closeMethod: 'if',
    fixedHeight: 120,
    scrollBar: 'auto',
    exitButtonText: null,
  },
)

defineEmits<{
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'update:visible': [visible: boolean]
}>()

// 為了避免不同裝置傳的東西不同，80 以上的直接改寫 full
function getVHDescWithGA(vh: number): string {
  return Math.round(vh) >= 80 ? 'full' : Math.round(vh).toString()
}

const DRAG_BAR_HEIGHT = 32
const isResizing = ref(false)
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const panelVh = ref<number>(props.defaultVh)
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const oldPanelVh = ref<number>(props.defaultVh)
addEventListener('pointerup', () => {
  isResizing.value = false
  removeEventListener('pointermove', updateValue)

  if (panelVh.value < 45) {
    panelVh.value = 25
  } else if (panelVh.value < 80) {
    panelVh.value = 50
  } else {
    // spec 為 100dvh - 44px
    panelVh.value = ((window.innerHeight - 44) / window.innerHeight) * 100
  }

  // 當滑鼠放開的時候，會去檢查 panel 有沒有變化，如果變了就會回傳 GA data 回去
  if (panelVh.value !== oldPanelVh.value && props.visible) {
    const oldVH = getVHDescWithGA(oldPanelVh.value)
    const newVH = getVHDescWithGA(panelVh.value)
    if (oldVH !== newVH) {
      pushGA4ButtonClick(`${props.panelKey} panel drag ${oldVH} to ${newVH}`)
    }
  }
  oldPanelVh.value = panelVh.value
})

function startDrag(): void {
  if (!props.isResizable) {
    return
  }
  addEventListener('pointermove', updateValue)
}

function updateValue(e: MouseEvent | PointerEvent): void {
  isResizing.value = true
  panelVh.value =
    ((window.innerHeight - e.clientY + DRAG_BAR_HEIGHT / 2) / window.innerHeight) * 100
}

const contentElement = useTemplateRef<HTMLElement>('contentElement')

onMounted(() => {
  if (contentElement.value !== null && props.scrollBar !== 'auto' && props.scrollBar !== 'hidden') {
    setMouseMoveEvent(contentElement.value, props.scrollBar)
  }
})

// 因為用了 dynamic component 希望捲軸位置可以固定住
// onDeactivated 的時候紀錄捲軸位置
// onActivated 的時候更新卷軸位置
const cacheScrollTop = ref<number>(0)
onActivated(() => {
  if (contentElement.value === null) {
    return
  }
  contentElement.value.scrollTop = cacheScrollTop.value
})
onDeactivated(() => {
  if (contentElement.value === null) {
    return
  }
  cacheScrollTop.value = contentElement.value.scrollTop
})

defineExpose({
  contentElement,
})
</script>

<style scoped>
/* TODO: 現在 scrollbar-hide 不能放在這邊，所以只好使用的時候要一併補上 scrollbar-hide 這個 class */
.header-slot :deep(.header-tab) {
  @apply inline-flex gap-24px overflow-x-auto overflow-y-hidden mx-8px;
}
.header-slot :deep(.header-tab .tab-item) {
  @apply title-12-h16-m text-primary-50 cursor-pointer text-nowrap;
}
.header-slot :deep(.header-tab .tab-item.tab-active) {
  @apply text-primary-100 pb-6px border-b-2 border-brand-deep-yellow;
}

.header-slot :deep(.header-title) {
  @apply title-12-h16-m text-primary-100;
}
</style>
